import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';

export interface BadgeDefinition {
  event: NostrEvent;
  d: string;
  name?: string;
  description?: string;
  image?: string;
  imageSize?: string;
  thumbs: { url: string; size?: string }[];
}

function parseBadgeDefinition(event: NostrEvent): BadgeDefinition | null {
  const d = event.tags.find(([t]) => t === 'd')?.[1];
  if (!d) return null;

  const name = event.tags.find(([t]) => t === 'name')?.[1];
  const description = event.tags.find(([t]) => t === 'description')?.[1];
  const imageTag = event.tags.find(([t]) => t === 'image');
  const image = imageTag?.[1];
  const imageSize = imageTag?.[2];
  const thumbs = event.tags
    .filter(([t]) => t === 'thumb')
    .map(([, url, size]) => ({ url, size }));

  return { event, d, name, description, image, imageSize, thumbs };
}

/** Fetch badge definitions (kind 30009) for a specific pubkey */
export function useBadgeDefinitions(pubkey: string | undefined) {
  const { nostr } = useNostr();

  return useQuery<BadgeDefinition[]>({
    queryKey: ['nostr', 'badge-definitions', pubkey ?? ''],
    queryFn: async (c) => {
      if (!pubkey) return [];

      const events = await nostr.query(
        [{ kinds: [30009], authors: [pubkey], limit: 100 }],
        { signal: c.signal },
      );

      return events
        .map(parseBadgeDefinition)
        .filter((b): b is BadgeDefinition => b !== null);
    },
    enabled: !!pubkey,
    staleTime: 60_000,
  });
}

/** Fetch a single badge definition by author + d tag */
export function useBadgeDefinition(pubkey: string | undefined, d: string | undefined) {
  const { nostr } = useNostr();

  return useQuery<BadgeDefinition | null>({
    queryKey: ['nostr', 'badge-definition', pubkey ?? '', d ?? ''],
    queryFn: async (c) => {
      if (!pubkey || !d) return null;

      const [event] = await nostr.query(
        [{ kinds: [30009], authors: [pubkey], '#d': [d], limit: 1 }],
        { signal: c.signal },
      );

      if (!event) return null;
      return parseBadgeDefinition(event);
    },
    enabled: !!pubkey && !!d,
    staleTime: 60_000,
  });
}
